import { initHeroScroll } from "./hero-scroll";

type HeroTranslations = {
    start: string;
    pause: string;
    startVideo: string;
    pauseVideo: string;
};

type HeroSettings = {
    displayMode: string;
    wavePosition: string;
};

function getTranslations(): HeroTranslations {
    const el = document.getElementById("hero-block-translations");
    if (!el) {
        return {
            start: "start",
            pause: "pause",
            startVideo: "Start video",
            pauseVideo: "Pause video",
        };
    }

    try {
        return JSON.parse(el.textContent ?? "") as HeroTranslations;
    } catch {
        return {
            start: "start",
            pause: "pause",
            startVideo: "Start video",
            pauseVideo: "Pause video",
        };
    }
}

function debounce<T extends (...args: any[]) => void>(fn: T, waitMs: number): T {
    let t: number | undefined;
    return ((...args: any[]) => {
        if (t !== undefined) {
            window.clearTimeout(t);
        }
        t = window.setTimeout(() => {
            fn(...args);
        }, waitMs);
    }) as T;
}

function prefersReducedMotion(): boolean {
    return !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function getHeroSettings(heroBlock: HTMLElement): HeroSettings {
    const heroId = heroBlock.dataset.heroId;
    if (!heroId) {
        return {
            displayMode: "",
            wavePosition: "",
        };
    }

    const inputs = document.querySelectorAll<HTMLInputElement>(".hero-data-input");
    for (const input of inputs) {
        if (input.value === heroId) {
            return {
                displayMode: input.dataset.displayMode ?? "",
                wavePosition: input.dataset.wavePosition ?? "",
            };
        }
    }

    return {
        displayMode: "",
        wavePosition: "",
    };
}

function applyPageSettings(heroBlock: HTMLElement): void {
    const settings = getHeroSettings(heroBlock);
    const waveBottom = settings.wavePosition.toLowerCase() === "bottom";
    const lowDisplayMode = settings.displayMode.toLowerCase() === "low";

    heroBlock.querySelector<HTMLElement>(".hero")?.classList.toggle("wave-bottom", waveBottom);
    heroBlock.querySelector<HTMLElement>(".hero__scroll-container")?.classList.toggle("wave-bottom", waveBottom);
    heroBlock.querySelector<HTMLElement>(".hero__inner")?.classList.toggle("wave-bottom", waveBottom);
    heroBlock.querySelector<HTMLElement>(".hero-bg-image")?.classList.toggle("ratio25", lowDisplayMode);
    heroBlock.querySelector<HTMLElement>(".hero-bg-video")?.classList.toggle("ratio25", lowDisplayMode);
}

function setScrollContainerHeight(heroRoot: HTMLElement): void {
    const scrollContainer = heroRoot.querySelector<HTMLElement>(".hero__scroll-container");
    if (!scrollContainer) {
        return;
    }

    const height = heroRoot.offsetHeight;
    scrollContainer.style.height = `${height}px`;

    const img = heroRoot.querySelector<HTMLImageElement>(".hero__cover img");
    if (img) {
        img.width = window.innerWidth;
        img.height = height;
    }
}

function setupVideoAutoplay(heroRoot: HTMLElement): void {
    const video = heroRoot.querySelector<HTMLVideoElement>(".hero__cover video, .hero-bg-video video");
    if (!video) {
        return;
    }

    if (prefersReducedMotion()) {
        video.removeAttribute("autoplay");
        video.pause();
        return;
    }

    video.setAttribute("autoplay", "autoplay");
    video.play().catch(() => {
        // Ignore autoplay blocks.
    });
}

function setupVideoControls(heroRoot: HTMLElement, translations: HeroTranslations): void {
    const video = heroRoot.querySelector<HTMLVideoElement>(".hero__cover video, .hero-bg-video video");
    const playButton = heroRoot.querySelector<HTMLButtonElement>(".video-controls__play-button");
    const text = heroRoot.querySelector<HTMLElement>(".video-controls__play-button .ctrl-text");

    if (!video || !playButton || !text) {
        return;
    }

    let playing = true;
    text.textContent = translations.pause;
    playButton.setAttribute("aria-label", translations.pauseVideo);

    if (prefersReducedMotion()) {
        playing = false;
        text.textContent = translations.start;
        playButton.setAttribute("aria-label", translations.startVideo);
        playButton.classList.remove("playing");
        playButton.classList.add("paused");
        return;
    }

    playButton.addEventListener("click", (event) => {
        event.preventDefault();
        playing = !playing;
        text.textContent = playing ? translations.pause : translations.start;
        playButton.setAttribute("aria-label", playing ? translations.pauseVideo : translations.startVideo);
        playButton.classList.remove(playing ? "paused" : "playing");
        playButton.classList.add(playing ? "playing" : "paused");
        if (playing) {
            video.play().catch(() => {});
        } else {
            video.pause();
        }
    });
}

export function initHero(heroRoot: HTMLElement): () => void {
    heroRoot.classList.add("is-mounted");

    const updateSize = debounce(() => {
        setScrollContainerHeight(heroRoot);
    }, 500);

    window.addEventListener("resize", updateSize);

    // If an image exists, wait for it before sizing
    const img = heroRoot.querySelector<HTMLImageElement>(".hero__cover img");
    if (img && !img.complete) {
        img.addEventListener(
            "load",
            () => {
                updateSize();
            },
            { once: true },
        );
    }

    updateSize();
    setupVideoAutoplay(heroRoot);
    setupVideoControls(heroRoot, getTranslations());

    // Init any hero-scroll wrappers inside this hero.
    const cleanups: Array<() => void> = [];
    const scrollWrappers = heroRoot.querySelectorAll<HTMLElement>(".hero-scroll");
    scrollWrappers.forEach((el) => {
        cleanups.push(initHeroScroll(el));
    });

    return () => {
        window.removeEventListener("resize", updateSize);
        cleanups.forEach((fn) => {
            fn();
        });
    };
}

export function initBackgroundOnlyHero(heroBlock: HTMLElement): () => void {
    applyPageSettings(heroBlock);
    setupVideoAutoplay(heroBlock);
    setupVideoControls(heroBlock, getTranslations());

    const cleanups: Array<() => void> = [];
    const scrollWrappers = heroBlock.querySelectorAll<HTMLElement>(".hero-scroll");
    scrollWrappers.forEach((el) => {
        cleanups.push(initHeroScroll(el));
    });

    return () => {
        cleanups.forEach((fn) => {
            fn();
        });
    };
}

// init all heroes on the page:
export function initAllHeros(): void {
    document.querySelectorAll<HTMLElement>(".hero-block").forEach((heroBlock) => {
        applyPageSettings(heroBlock);

        const hero = heroBlock.querySelector<HTMLElement>(".hero");
        if (hero) {
            initHero(hero);
            return;
        }

        if (heroBlock.querySelector(".hero-bg-image, .hero-bg-video")) {
            initBackgroundOnlyHero(heroBlock);
        }
    });
}

document.addEventListener("DOMContentLoaded", () => {
    initAllHeros();
});
